import { styled } from '@/stitches.config';

import { Box } from '@base-app';
import { DropdownVariant } from './Dropdown.types';
import { Sizes } from '@common/types';
import { ReactComponent as DropdownIcon } from '@assets/icons/dropdown.svg';
import { ReactComponent as DropdownTableIcon } from '@assets/icons/table-dropdown.svg';

export const DropdownContainerStyled = styled(Box, {
  position: 'relative',
});

export const DropdownButtonStyled = styled('button', {
  $$textDropdown: '$colors$blue300',
  $$textDropdownActive: '$colors$blue200',
  $$textDropdownDisabled: '$colors$darkGray300',
  all: 'unset',
  alignItems: 'center',
  display: 'flex',
  borderRadius: '$3',
  boxSizing: 'border-box',
  textAlign: 'center',
  fontSize: '$md',
  justifyContent: 'center',
  color: '$$textDropdown',
  fontWeight: '$medium',
  px: '$0',
  py: '$0',
  width: 'auto',
  '&::before': {
    boxSizing: 'border-box',
  },
  '&::after': {
    boxSizing: 'border-box',
  },
  '&:hover': {
    cursor: 'pointer',
  },
  '&:hover, &:focus': {
    color: '$textLink',
    textDecoration: 'underline',
  },
  '&:active': {
    color: '$$textDropdownActive',
  },
  '&:disabled': {
    background: 'none',
    color: '$$textDropdownDisabled',
    textDecoration: 'none',
    cursor: 'not-allowed',
  },
  '&:focus': {
    outlineColor: '$focusOutline',
    outlineStyle: 'solid',
    outlineWidth: '$3',
  },
  variants: {
    size: {
      [Sizes.SMALL]: {},
      [Sizes.MEDIUM]: {},
      [Sizes.LARGE]: {},
      [Sizes.EXTRA_LARGE]: {},
      [Sizes.XX_LARGE]: {},
      [Sizes.XXX_LARGE]: {},
      [Sizes.XXXX_LARGE]: {},
    },
    variant: {
      [DropdownVariant.DEFAULT]: {},
      [DropdownVariant.TABLE]: {},
      [DropdownVariant.PILL]: {
        background: '$coolGray300',
        border: '1px solid $darkGray700',
        borderRadius: '$pill',
        color: '$darkGray200',
        cursor: 'pointer',
        fontSize: '$md',
        fontWeight: '$bold',
        padding: '$2 $2',
        whiteSpace: 'pre',
        '&:hover, &:focus': {
          color: 'inherit',
          textDecoration: 'none',
        },
        '&:focus': {
          outline: 'none',
        },
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
  },
  compoundVariants: [
    {
      variant: DropdownVariant.PILL,
      size: Sizes.SMALL,
      css: {
        fontSize: '$xs',
        lineHeight: '$4',

        svg: {
          height: '$4',
          width: '$4',
        },
      },
    },
    {
      variant: DropdownVariant.PILL,
      size: Sizes.MEDIUM,
      css: {
        alignItems: 'center',
        display: 'flex',
        fontSize: '$xs',
        height: '$2',
        lineHeight: '$4',
        padding: '$3 $3',

        svg: {
          height: '$4',
          width: '$4',
        },
      },
    },
  ],
});

export const DropdownListStyled = styled('ul', {
  backgroundColor: '$white',
  borderRadius: '$3',
  boxShadow: '$menu',
  listStyleType: 'none',
  margin: '$0',
  minWidth: '$dropdownListWidth',
  position: 'absolute',
  px: '$0',
  py: '$3',
  textAlign: 'left',
  zIndex: '$dropdown',

  variants: {
    size: {
      [Sizes.SMALL]: {
        minWidth: 'max-content',
        padding: '$3',
      },
      [Sizes.MEDIUM]: {
        padding: '$3',
        minWidth: 'auto',
      },
      [Sizes.LARGE]: {},
      [Sizes.EXTRA_LARGE]: {},
      [Sizes.XX_LARGE]: {},
      [Sizes.XXX_LARGE]: {},
      [Sizes.XXXX_LARGE]: {},
    },
    variant: {
      [DropdownVariant.DEFAULT]: {},
      [DropdownVariant.TABLE]: {},
      [DropdownVariant.PILL]: {},
    },
  },
  compoundVariants: [
    {
      variant: DropdownVariant.PILL,
      size: Sizes.SMALL,
      css: {
        flexDirection: 'column',
        minWidth: '100%',
      },
    },
  ],
});

export const DropdownListItemStyled = styled('li', {
  px: '$3',
  py: '$2',
  whiteSpace: 'nowrap',
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: '$coolGray300',
  },
  '&.selected': {
    fontWeight: '$medium',
  },
});

export const DropdownIconStyled = styled(DropdownIcon, {
  height: '100%',
  marginLeft: '$1',
  variants: {
    size: {
      [Sizes.SMALL]: {},
      [Sizes.MEDIUM]: {},
      [Sizes.LARGE]: {},
      [Sizes.EXTRA_LARGE]: {},
      [Sizes.XX_LARGE]: {},
      [Sizes.XXX_LARGE]: {},
      [Sizes.XXXX_LARGE]: {},
    },
    variant: {
      [DropdownVariant.DEFAULT]: {},
      [DropdownVariant.TABLE]: {},
      [DropdownVariant.PILL]: {
        height: '$6',
        marginLeft: '$1',
        color: '$darkGray200',
        width: '$6',
      },
    },
  },
  compoundVariants: [
    {
      variant: DropdownVariant.PILL,
      size: Sizes.SMALL,
      css: {
        height: '$6',
        marginLeft: '$1',
        color: '$darkGray200',
        width: '$6',
      },
    },
  ],
});

export const DropdownIconRotatedStyled = styled(DropdownIconStyled, {
  rotate: '180deg',
});

export const DropdownTableIconStyled = styled(DropdownTableIcon, {
  color: '$darkGray200',
  height: '100%',
  marginLeft: '$1',
});

export const DropdownTableIconRotatedStyled = styled(DropdownTableIconStyled, {
  rotate: '180deg',
});
